import { monthLabel } from '../model/months'
import type { SeriesPoint } from '../model/portfolioOverview'
import type { MonthKey } from '../model/types'
import { countOf, joinWithAnd, monthSpanText } from '../portfolioText'

// Up to this many points, a gap names its account on the axis.
export const MAX_NAMED_AXIS_POINTS = 6
const MAX_NAMED_GAPS = 4
const MAX_NAMED_ACCOUNTS = 3

export interface ChartGap {
  key: string
  // A point index, or a half step between two points for months no statement ends in.
  position: number
  months: MonthKey[]
  accountNames: string[]
  label: string
  // Only these labels go under the axis. The rest would read as a second row of month labels.
  namesAccount: boolean
}

const unique = (values: string[]) => Array.from(new Set(values)).sort()

const monthsText = (months: MonthKey[]) =>
  monthSpanText(months[0], months[months.length - 1])

const gapAtPoint = (
  point: SeriesPoint,
  index: number,
  pointCount: number,
): ChartGap => {
  const accountNames = unique(point.missing.map((entry) => entry.accountName))
  const namesOne =
    pointCount <= MAX_NAMED_AXIS_POINTS && accountNames.length === 1
  return {
    key: point.month,
    position: index,
    months: [point.month],
    accountNames,
    namesAccount: namesOne,
    label: namesOne
      ? `No ${accountNames[0]} statement`
      : monthLabel(point.month),
  }
}

// Months between two points that no statement ends in, yet some account should have one.
const gapBefore = (point: SeriesPoint, index: number): ChartGap | null => {
  const hidden = point.notCounted.filter(
    (entry) => entry.reason === 'no_statement' && entry.month < point.month,
  )
  if (index === 0 || hidden.length === 0) return null
  const months = unique(hidden.map((entry) => entry.month))
  return {
    key: `before-${point.month}`,
    position: index - 0.5,
    months,
    accountNames: unique(hidden.map((entry) => entry.accountName)),
    label: monthsText(months),
    namesAccount: false,
  }
}

export const chartGaps = (series: SeriesPoint[]): ChartGap[] =>
  series.flatMap((point, index) => {
    const before = gapBefore(point, index)
    const atPoint = point.hasGap
      ? [gapAtPoint(point, index, series.length)]
      : []
    return before ? [before, ...atPoint] : atPoint
  })

const namesText = (names: string[]) => {
  const shown = names.slice(0, MAX_NAMED_ACCOUNTS)
  const more = names.length - shown.length
  return joinWithAnd(
    more > 0 ? [...shown, countOf(more, 'more account')] : shown,
  )
}

// "Hatched months have a missing statement: Nov 2022 from Charles Schwab •• 8817, so it has no total."
export const gapNote = (gaps: ChartGap[]): string | null => {
  if (gaps.length === 0) return null
  const named = gaps
    .slice(0, MAX_NAMED_GAPS)
    .map(
      (gap) => `${monthsText(gap.months)} from ${namesText(gap.accountNames)}`,
    )
  const more = gaps.length - named.length
  const list = more > 0 ? [...named, countOf(more, 'more gap')] : named
  const monthCount = gaps.reduce((sum, gap) => sum + gap.months.length, 0)
  const ending =
    monthCount === 1
      ? 'so that month has no total'
      : 'so those months have no total'
  return `Hatched months have a missing statement: ${joinWithAnd(list)}, ${ending}.`
}
